import React, { useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Typography, Button, Box, Stack, CircularProgress, Badge, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, useMediaQuery } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { AppContext } from "./Store";
import { useSnackbar } from "./shared/SnackbarProvider";
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import CarIcon from '@mui/icons-material/DirectionsCar';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';

import { auth, analytics } from "./firebase-config";
import { logEvent, setUserId, setDefaultEventParameters } from "firebase/analytics";
import { indexedDBLocalPersistence, GoogleAuthProvider, AuthProvider, sendSignInLinkToEmail, signInWithEmailAndPassword, onAuthStateChanged, setPersistence, signInWithPopup, sendPasswordResetEmail } from "firebase/auth";
import { log } from "console";

const Menu: React.FC = () => {

  const context = useContext(AppContext);

  const { user, setUser, observationData, userContext, portalType } = context;
  const navigate = useNavigate();
  const snack = useSnackbar();

  const [open, setOpen] = React.useState(false);
  const [loginStep, setLoginStep] = React.useState<"login" | "forgot" | "sent">("login");

  // Added state for admin drawer
  const [adminMenuOpen, setAdminMenuOpen] = React.useState(false);

  const handleAdminMenuOpen = () => setAdminMenuOpen(true);
  const handleAdminMenuClose = () => setAdminMenuOpen(false);

  useEffect(() => {
    // Development mode bypass
    if (import.meta.env.DEV) {
      console.log("🔧 Development mode: Bypassing authentication");
      const mockUser = {
        uid: "dev-mock-user",
        email: "dev@bee-happy.club",
        emailVerified: true,
        displayName: "Dev User",
        photoURL: null,
      } as any;
      setUser(mockUser);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("onAuthStateChanged", currentUser);
      setUser(currentUser);
      if (portalType === "parent") {
        setOpen(!currentUser);
      }
    });

    return () => unsubscribe();

  }, []); // Added empty dependency array to prevent infinite loop

  const logOut = () => {
    logEvent(analytics, "sign_out");
    setUser(null);
    auth.signOut()
    navigate("/");
  };

  const signInForParentsWithEmailAndPassword = async (email: string, password: string) => {

    setDefaultEventParameters({
      portalType,
      method: "EmailAndPassword",
    });

    logEvent(analytics, "sign_in_attempt", {
      email
    });

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      setUserId(analytics, email);
      logEvent(analytics, "sign_in_success");

      console.log({ result });

      snack("Signed in", "info");

    } catch (error) {
      snack("Error signing in. Please try again later.", "error");

      logEvent(analytics, "sign_in_error", {
        email,
        error: error instanceof Error ? error.message : String(error)
      });

      console.error("Error signing in:", error);
    }
  }

  const signInForParentsWithEmailLink = async (email: string) => {

    console.log("Signing in with Email");
    const actionCodeSettings = {
      url: 'https://parents.preschool.bee-happy.club/finishSignUp',
      handleCodeInApp: true
    };

    console.log("Sending sign in link to", email);

    await sendSignInLinkToEmail(auth, email, actionCodeSettings)

    window.localStorage.setItem('emailForSignIn', email);
    snack("Email with log in link sent. Please check your inbox.", "info");
  }

  const signInWithGoogle = async () => {

    try {
      console.log("Signing in with Google");

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" })

      setDefaultEventParameters({ portalType, method: "Google" });

      logEvent(analytics, "sign_in_attempt");

      const result = await signInWithPopup(auth, provider);

      setUserId(analytics, result.user.email);

      logEvent(analytics, "sign_in_success");

      snack("Signed in as " + result.user.displayName, "success");
    } catch (error) {
      logEvent(analytics, "sign_in_error", {
        error: error instanceof Error ? error.message : String(error),
        portalType,
        method: "Google"
      });
    }
  }

  const signIn = async () => {


    try {
      await setPersistence(auth, indexedDBLocalPersistence);

      let provider: AuthProvider = null;
      if (portalType === "teacher") {
        await signInWithGoogle();
      }

      if (portalType === "parent") {
        handleClickOpen();
      }

    } catch (error) {
      console.error(error);
      snack("Error signing in. Please try again later.", "error");
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoginStep("login");
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={adminMenuOpen}
        onClose={handleAdminMenuClose}
      >
        <List>
          <ListItemButton component={Link} to="/driving" onClick={handleAdminMenuClose}>
            <ListItemIcon>
              <CarIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Driving" primaryTypographyProps={{ color: 'primary' }} />
          </ListItemButton>
          <ListItemButton component={Link} to="/stickers" onClick={handleAdminMenuClose}>
            <ListItemIcon>
              <LocalOfferIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Stickers" primaryTypographyProps={{ color: 'primary' }} />
          </ListItemButton>
          <ListItemButton component={Link} to="/customers" onClick={handleAdminMenuClose}>
            <ListItemIcon>
              <PeopleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Customers" primaryTypographyProps={{ color: 'primary' }} />
          </ListItemButton>
        </List>
      </Drawer>
      <AppBar position="static" style={{ marginBottom: "15px" }} className="no-print">
        <Toolbar className="no-print">
          {/* Conditionally render admin slide out menu button */}
          {userContext?.isAdmin() && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="admin menu"
              onClick={handleAdminMenuOpen}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Link to="/">
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
            >
              <HomeIcon />
            </IconButton>
          </Link>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {user?.displayName ? `Welcome, ${user.displayName}` : "Bee Happy PreSchool"}
          </Typography>
          {useMediaQuery("(min-width: 720px)") &&
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {portalType === "teacher" ? "Teacher Portal" : "Parent Portal"}
            </Typography>
          }
          {userContext?.isAdmin() && (
            <Link to="/observations">
              <IconButton
                size="large"
                color="inherit"
              >
                <Badge badgeContent={observationData?.filter(x => !x.reviewed).length} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Link>
          )}
          {
            user &&
            <Button color="inherit" onClick={logOut}>
              Log out
            </Button>
          }
          {!user && (
            <React.Fragment>
              <Stack spacing={2}>
                <Box sx={{ m: 1, position: "relative" }}>
                  <Button onClick={signIn} variant="contained" color="primary" fullWidth>
                    Log in
                  </Button>
                </Box>
              </Stack>
            </React.Fragment>
          )}
          <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
              component: 'form',
              onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();

                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries((formData as any).entries());
                const email = formJson.email;
                const password = formJson.password;

                if (loginStep === "forgot") {

                  logEvent(analytics, "password_reset_requested", { email, portalType, method: "EmailAndPassword" });

                  await sendPasswordResetEmail(auth, email);
                  logEvent(analytics, "password_reset_email_sent", { email, portalType, method: "EmailAndPassword" });
                  setLoginStep("sent");
                } else {
                  await signInForParentsWithEmailAndPassword(email, password);
                  handleClose();
                }
              },
            }}
          >
            <DialogTitle>Log in</DialogTitle>
            <DialogContent>
              {loginStep === "login" &&
                <DialogContentText>
                  If this is your <b>first time</b> logging in, please use 'forgot password' option.
                </DialogContentText>
              }
              {loginStep === "forgot" &&
                <DialogContentText>
                  Please enter your email address to reset your password.
                  <br />
                  If you have not received an email with a password reset link, please <b>check your spam/junk folder</b>.
                </DialogContentText>
              }
              {loginStep === "sent" &&
                <DialogContentText>
                  Password reset link has been sent to your email.
                  <br />
                  Please check your inbox and spam/junk folder.
                  <br />
                  <br />
                  Come back to this page after you have reset your password.
                </DialogContentText>
              }
              <TextField
                autoFocus
                required
                margin="dense"
                id="name"
                name="email"
                label="Email Address"
                type="email"
                fullWidth
                variant="standard"
              />
              {loginStep !== "forgot" && (
                <TextField
                  required
                  margin="dense"
                  id="name"
                  name="password"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="standard"
                />
              )}
            </DialogContent>
            <DialogActions>
              {loginStep !== "forgot" &&
                <Button onClick={() => setLoginStep("forgot")}>Forgot password?</Button>
              }
              <Button color="primary" variant="contained" type="submit">{loginStep === "forgot" ? "Reset password" : "Log in"}</Button>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Menu;
